import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { School } from '@teetsh/app/src/types/school';
import { SCHOOLS_KEY } from '@teetsh/app/src/queries/schools/useSchools';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { LAST_SCHOOLYEAR_SCHOOL_KEY } from '@teetsh/app/src/queries/schoolyears/useLastSchoolyearSchool';
import { useTranslation } from 'react-i18next';
import { SCHOOLYEARS_KEY } from '@teetsh/app/src/queries/schoolyears/useSchoolyears';
import { Schoolyear } from '@teetsh/app/src/types/schoolyear';

function updateSchool({
  schoolyearId,
  school,
}: {
  schoolyearId: string;
  school: School;
}) {
  return request(`/schoolyears/${schoolyearId}/schools/${school.id}`, {
    method: 'PATCH',
    body: school,
  });
}

export default function useUpdateSchool(schoolyearId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['schoolyearSettings']);

  const { notifyError } = useNotification();
  const { mutateAsync } = useMutation({
    mutationFn: (school: School) => updateSchool({ schoolyearId, school }),

    onMutate: async (updatedSchool: School) => {
      const queryKey = [SCHOOLYEARS_KEY, schoolyearId, { withSchools: true }];
      await queryClient.cancelQueries({ queryKey });

      const previousSchoolyear = queryClient.getQueryData<Schoolyear>(queryKey);

      if (previousSchoolyear?.schools) {
        queryClient.setQueryData<Schoolyear>(queryKey, {
          ...previousSchoolyear,
          schools: (previousSchoolyear?.schools || []).map(school =>
            school.id === updatedSchool.id ? updatedSchool : school,
          ),
        });
      }

      return { previousSchoolyear };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SCHOOLS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [LAST_SCHOOLYEAR_SCHOOL_KEY],
      });
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSchoolyear) {
        queryClient.setQueryData(
          [SCHOOLYEARS_KEY, schoolyearId, { withSchools: true }],
          context.previousSchoolyear,
        );
      }
      notifyError({
        title: t('schoolyearSettings:messages.school.updateError'),
      });
    },
  });
  return mutateAsync;
}
